import React, { useState } from 'react';
import { Tag, Heading, Lead, PageHero, Section, BtnPrimary } from '../components/UI';
import styles from './Training.module.css';

const COURSES = [
  {
    n: '01', t: 'Java Full Stack',
    desc: 'A 7-week, project-driven program covering Core Java, JDBC, Servlets/JSP, Hibernate, Spring Boot, Spring Security, and Angular — capped off with a real AWS-deployed capstone project and interview preparation.',
    topics: ['Core Java 8 & OOPs', 'Collections & Java 8 Features', 'JDBC & MySQL', 'Servlets, JSP & Hibernate', 'Spring, Spring Security & Spring Boot', 'HTML5, CSS3, Bootstrap', 'JavaScript, TypeScript & Angular', 'AWS Deployment & CI/CD'],
    duration: '7 Weeks', mode: 'Online',
    pdf: '/course-pdfs/java-full-stack.pdf',
  },
  {
    n: '02', t: '.Net Full Stack',
    desc: 'A comprehensive .NET training program covering ASP.NET Web Forms, C# programming and OOP, ASP.NET MVC & Web API, WCF services, and WPF desktop development — from core language fundamentals to enterprise architecture.',
    topics: ['ASP.NET Web Forms', 'C# & OOP', 'ADO.NET & Data Access', 'ASP.NET MVC & Web API', 'WCF Services', 'WPF Desktop Apps'],
    duration: '4 Months', mode: 'Online',
    pdf: '/course-pdfs/dot-net.pdf',
  },
  {
    n: '03', t: 'Python Full Stack',
    desc: 'A hands-on Python programming course covering language fundamentals, OOP, file handling, and libraries — building up to full-stack web development with the Django framework.',
    topics: ['Python Fundamentals', 'Data Types & Control Flow', 'Functions, Modules & OOP', 'File Handling & Error Handling', 'Libraries & Regular Expressions', 'Django Web Framework'],
    duration: '3.5 Months', mode: 'Online',
    pdf: '/course-pdfs/python.pdf',
  },
  {
    n: '04', t: 'AI & Machine Learning',
    desc: 'Practical training in Artificial Intelligence, Machine Learning, Deep Learning, Generative AI, NLP, and computer vision with Python-based hands-on projects — from data preprocessing to MLOps and deployment.',
    topics: ['Python & Math for ML', 'Supervised & Unsupervised ML', 'Deep Learning & CNNs', 'NLP & Transformers', 'Generative AI & LLMs', 'AI Agents', 'MLOps & Deployment', 'Capstone Projects'],
    duration: '4-6 Months', mode: 'Online',
    pdf: '/course-pdfs/ai-ml.pdf',
  },
  {
    n: '05', t: 'Salesforce',
    desc: 'Comprehensive Salesforce development training covering Apex programming, Visualforce, and Lightning Components (Aura & LWC) — from platform fundamentals to building production-ready applications.',
    topics: ['Salesforce & Force.com Basics', 'Apex Programming', 'SOQL, SOSL & Triggers', 'Visualforce', 'Lightning (Aura) Components', 'Lightning Web Components'],
    duration: '3 Months', mode: 'Online',
    pdf: '/course-pdfs/salesforce.pdf',
  },
  {
    n: '06', t: 'Power BI',
    desc: 'A Zero-to-Advanced, project-based Power BI course — load and transform data, build data models, create rich visualizations and DAX measures, and publish interactive dashboards, with a real Formula One analytics project.',
    topics: ['Data Loading & Transformation', 'Data Modeling', 'Visualizations & Charts', 'DAX & Data Analysis', 'Power BI Service & Dashboards', 'SQL Server for Power BI', 'AI Visuals & CoPilot', 'Real-World Projects'],
    duration: '2 Months', mode: 'Online',
    pdf: '/course-pdfs/power-bi.pdf',
  },
  {
    n: '07', t: 'QA Automation',
    desc: 'A job-oriented QA Automation program covering testing fundamentals, JavaScript/TypeScript, Playwright, Cypress, API and database testing, Git/CI-CD, framework design, and real-time projects.',
    topics: ['Testing Fundamentals', 'JavaScript & TypeScript', 'Playwright Automation', 'Cypress Automation', 'API & SQL Testing', 'Git, CI/CD & Real-Time Projects'],
    duration: '130+ Hrs', mode: 'Online',
    pdf: '/course-pdfs/qa-automation.pdf',
  },
  {
    n: '08', t: 'DevOps',
    desc: 'Streamline software delivery with CI/CD pipelines, containerization, infrastructure as code, and cloud-native operations.',
    topics: ['Linux & Shell Scripting', 'Git & CI/CD (Jenkins)', 'Docker & Kubernetes', 'Terraform / IaC', 'AWS / Azure DevOps', 'Monitoring & Logging'],
    duration: '3 Months', mode: 'Online',
  },
  {
    n: '09', t: 'Data Engineering',
    desc: 'Design, build, and manage scalable data pipelines and architectures using modern big data and cloud tools.',
    topics: ['SQL & Python for Data', 'ETL Pipelines', 'Apache Spark', 'Data Warehousing', 'Airflow', 'Cloud Data Platforms'],
    duration: '3.5 Months', mode: 'Online',
  },
  {
    n: '10', t: 'Business Analyst',
    desc: 'Bridge business and technology with requirement gathering, process modeling, and stakeholder communication skills.',
    topics: ['Requirement Elicitation', 'BPMN & Process Modeling', 'Agile & Scrum', 'SQL Basics', 'Documentation (BRD/FRD)', 'Stakeholder Communication'],
    duration: '2.5 Months', mode: 'Online',
  },
];

const FEATURES = [
  { icon: '🎓', t: 'Certified Instructors',    d: 'All courses taught by certified and experienced industry professionals.' },
  { icon: '💻', t: 'Learn from Anywhere',      d: 'Fully online — everything done from your computer\'s desktop, on your schedule.' },
  { icon: '📚', t: 'Rich Course Content',       d: 'Exhaustive explanations, detailed coverage, and practical project-based learning.' },
  { icon: '💰', t: 'Competitive Pricing',       d: 'High-quality training at affordable rates with flexible payment options.' },
  { icon: '🏆', t: 'Industry Certification',   d: 'Prepare for globally recognized certifications that boost your career.' },
  { icon: '🤝', t: 'Career Support',            d: 'Job placement assistance and interview preparation for course graduates.' },
];

export default function Training() {
  const [selected, setSelected] = useState(0);
  const course = COURSES[selected];

  return (
    <>
      <PageHero
        tag="IT Training"
        title="Learn from Certified Industry Experts"
        subtitle="Metawave Solutions offers high-quality, state-of-the-art IT and business-related e-learning training courses — competitive prices, rich content, and learner-friendly delivery."
      />

      {/* ── Features ───────────────────────────────────────────── */}
      <Section variant="paper">
        <Tag>Why Train With Us</Tag>
        <Heading>What Makes Our Training Different</Heading>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f, i) => (
            <div key={i} className={styles.featureCard}>
              <div className={styles.featureIcon}>{f.icon}</div>
              <h3 className={styles.featureTitle}>{f.t}</h3>
              <p className={styles.featureDesc}>{f.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Course Explorer ─────────────────────────────────────── */}
      <Section variant="white">
        <Tag>Our Curriculum</Tag>
        <Heading>Courses Offered</Heading>
        <Lead>Ten industry-aligned programs designed to launch or advance your career in tech.</Lead>
        <div className={styles.explorer}>
          {/* Sidebar */}
          <div className={styles.courseList}>
            {COURSES.map((c, i) => (
              <button
                key={i}
                className={`${styles.courseBtn} ${selected === i ? styles.courseBtnActive : ''}`}
                onClick={() => setSelected(i)}
              >
                <span className={styles.courseBtnNum}>{c.n}</span>
                <span className={styles.courseBtnTitle}>{c.t}</span>
              </button>
            ))}
          </div>

          {/* Detail Panel */}
          <div className={styles.courseDetail} key={selected}>
            <div className={styles.detailMeta}>
              <span className={styles.metaBadge}>⏱ {course.duration}</span>
              <span className={styles.metaBadge}>🖥 {course.mode}</span>
            </div>
            <h2 className={styles.detailTitle}>{course.t}</h2>
            <p className={styles.detailDesc}>{course.desc}</p>

            <div className={styles.topicsSection}>
              <h4 className={styles.topicsHeading}>Topics Covered</h4>
              <div className={styles.topicsGrid}>
                {course.topics.map((topic, i) => (
                  <div key={i} className={styles.topicChip}>
                    <span className={styles.topicDot} />
                    {topic}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', marginTop: '2rem' }}>
              <BtnPrimary to="/contact-us">
                Enroll in {course.t} →
              </BtnPrimary>
            </div>
          </div>
        </div>
      </Section>

      {/* ── CTA ────────────────────────────────────────────────── */}
      {/* <Section variant="ink" style={{ textAlign: 'center' }}>
        <Tag light>Get Started Today</Tag>
        <Heading light>Ready to Advance Your Career?</Heading>
        <Lead light style={{ margin: '0 auto 2rem' }}>
          Join thousands of professionals who have upskilled with Metawave Solutions training programs.
        </Lead>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <BtnPrimary to="/contact">Enroll Now</BtnPrimary>
        </div>
      </Section> */}
    </>
  );
}
